import { useMemo } from 'react';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';
import Tooltip from '@sdk/common/tooltip/Tooltip';

import { QueryClientProvider } from '@web/components/QueryClientProvider/QueryClientProvider';
import { useAngularModalState } from '@web/common/hooks/useAngularModalState';
import { ShiftTasksBridgeState } from '@web/shiftTasks/ShiftTasksBridgeProvider/shiftTasksBridgeProvider.types';
import { ShiftTasksBridgeProvider } from '@web/shiftTasks/ShiftTasksBridgeProvider/ShiftTasksBridgeProvider';
import { TaskInstance } from '@web/shiftTasks/types/TaskInstance';

import { EditTaskInstance } from './EditTaskInstance';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  originalInstance: any;
  shiftTasksReactBridge: ShiftTasksBridgeState;

  // usTimeFormat and personasDict are passed from angular for now
  // to avoid fetching session just for the modal.
  usTimeFormat: boolean;
  personasDict: PersonasDict;
};

export const EditTaskInstanceAngular = ({
  isOpen,
  onClose,
  shiftTasksReactBridge,
  originalInstance,
  usTimeFormat,
  personasDict,
}: Props) => {
  const instance = useMemo(
    () => mapAngularInstanceToUI(originalInstance, personasDict),
    [originalInstance, personasDict]
  );
  const modalState = useAngularModalState(isOpen, onClose);

  return (
    <ShiftTasksBridgeProvider shiftTasksBridge={shiftTasksReactBridge}>
      <QueryClientProvider>
        {modalState.isVisible && (
          <EditTaskInstance
            modalState={modalState}
            originalInstance={instance}
            usTimeFormat={usTimeFormat}
          />
        )}

        <ToastComponent />
        <Tooltip />
      </QueryClientProvider>
    </ShiftTasksBridgeProvider>
  );
};

function mapAngularInstanceToUI(instance: any, personasDict: any): TaskInstance {
  if (!instance) {
    return null;
  }

  const {
    assigneeId,
    attachments,
    created,
    description,
    dtend,
    dtstart,
    id,
    openEnd,
    subtasks,
    status,
    title,
  } = instance;

  const assignee = assigneeId ? personasDict[assigneeId] : null;
  // fullname safety check is in case of deleted user or persona other than user
  // it shouldn't be needed, but with angular not being in typescript it's best to be safe.
  const instanceAssignee =
    assignee && assignee.fullname
      ? {
          id: assignee.id,
          fullname: assignee.fullname,
        }
      : null;

  return {
    assignee: instanceAssignee,
    attachments,
    createdAt: new Date(created),
    dtend: new Date(dtend),
    dtstart: new Date(dtstart),
    description,
    id,
    isOpenEnd: openEnd,
    subtasks,
    status,
    title,
  };
}
